import TextField, { OutlinedTextFieldProps } from "@mui/material/TextField";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { FastField, Field, getIn } from "formik";
import { inputStyles } from "./styles";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    input: {
      maxWidth: "100%",
      width: ({ width }: { width?: string }) => width || 373,
      ...inputStyles,
    },
  }),
);

type TextInputProps = Omit<OutlinedTextFieldProps, "variant"> & {
  "data-test-id"?: string;
  width?: string;
  maxLength?: number;
  adjustWidthToValue?: boolean;
  renderAsFastField?: boolean;
  validateOnBlur?: boolean;
  customOnBlur?: () => void;
};

export function TextInput({
  "data-test-id": dataTestId,
  width,
  maxLength,
  adjustWidthToValue,
  renderAsFastField,
  validateOnBlur,
  customOnBlur,
  ...props
}: TextInputProps) {
  const classes = useStyles({
    width: adjustWidthToValue ? "unset" : width,
  });
  const isEndAdornment = props.InputProps?.endAdornment;
  const textField = ({
    field,
    form,
    meta,
  }: {
    field: any;
    form: any;
    meta: any;
  }) => (
    <TextField
      {...field}
      {...props}
      className={`${classes.input} ${props.className}`}
      error={!!(meta.error || props.error || getIn(form.status, field.name))}
      helperText={
        meta.error ||
        props.error ||
        getIn(form.status, field.name) ||
        props.helperText
      }
      onBlur={(e) => {
        field.onBlur(e);
        if (form.status?.[field.name]) {
          const { [field.name]: _fieldError, ...rest } = form.status;
          form.setStatus(rest);
        }
        if (validateOnBlur) {
          form.validateField(field.name);
        }

        customOnBlur && customOnBlur();
      }}
      onChange={(e) => {
        props.onChange ? props.onChange(e) : field.onChange(e);
      }}
      onKeyDown={props.onKeyDown}
      value={props.value ? props.value : (field.value ?? "")}
      variant="outlined"
      inputProps={{
        ...props.inputProps,
        "data-test-id": dataTestId || "",
        ...(props.type === "number" && { min: 0 }),
        maxLength: maxLength,
        autoComplete: "off",
      }}
      sx={{
        label: {
          ...(isEndAdornment
            ? {
                maxWidth: "calc(100% - 48px)",
              }
            : {}),
        },
        input: {
          ...(isEndAdornment
            ? {
                paddingRight: 4,
              }
            : {}),
          width:
            adjustWidthToValue && field?.value
              ? field.value.toString().length + 1 + "ch"
              : "100%",
        },
      }}
    />
  );

  return renderAsFastField ? (
    <FastField name={props.name}>{textField}</FastField>
  ) : (
    <Field name={props.name}>{textField}</Field>
  );
}
